import { cn } from "@somethings-ui/core";
import { IconButton } from "@somethings-ui/web";
import { ChevronsLeft, ChevronsRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Hub } from "@/components/hub/hub";
import { useHubStore } from "@/components/hub/use-hub-store";
import type { MentorshipDetails } from "@/lib/api/mentorships";
import { useMentorWorkflowsSelection } from "../use-selection";
import { ActionFloatingAdd } from "./action-floating-add";
import { AiChatButton, AiChatPanel } from "./ai-chat";
import {
  MENTOR_LIVE_CAPABILITIES,
  type ActionSurfaceCapabilities,
} from "./action-capabilities";
import { ActionPanelScrollProvider } from "./action-panel-scroll";
import {
  ACTION_TABS,
  DEFAULT_ACTION_TAB,
  type ActionTab,
  type ActionTabConfig,
} from "./action-tab";
import { ContextTab } from "./context/context-tab";
import { HistoryTab } from "./history/history-tab";
import { ProfileTab } from "./profile/profile-tab";
import { useWallUpdates } from "./profile/use-wall-updates";
import { useMentorshipUserUpdates } from "./use-mentorship-user-updates";
import { ToolsTab } from "./tools/tools-tab";
import { useActionAnalytics } from "./use-action-analytics";
import { ActionLaunchProvider } from "./use-action-launch";
import { useActionMentorship } from "./use-action-mentorship";
import { useActionStore } from "./use-action-store";
import { useMenteeWallBadges } from "@/lib/api/walls";
import { useTeenWallEnabled } from "@/lib/hooks/use-teen-wall-enabled";
import { ColumnDivider } from "../column-divider";
import {
  ACTION_MAX,
  ACTION_MIN,
  ACTION_RAIL,
  previewColumnWidth,
  resolveColumnDrag,
} from "../column-layout";
import { useWorkflowColumnLayout } from "../use-workflow-columns";

export function ActionWorkspace() {
  const { selectedConversation } = useMentorWorkflowsSelection();
  const channelUrl = selectedConversation?.channel ?? "";

  const capabilities = MENTOR_LIVE_CAPABILITIES;
  const { menteeId, mentorshipId, menteeName, mentorship } =
    useActionMentorship(channelUrl);
  const analytics = useActionAnalytics(menteeId);

  const collapsed = useActionStore((s) => s.collapsed);
  const activeTab = useActionStore((s) => s.activeTab);
  const setTab = useActionStore((s) => s.setTab);
  const expand = useActionStore((s) => s.expand);
  const collapse = useActionStore((s) => s.collapse);
  const toggleCollapse = useActionStore((s) => s.toggleCollapse);
  const aiChatOpen = useActionStore((s) => s.aiChatOpen);
  const openAiChat = useActionStore((s) => s.openAiChat);
  const closeAiChat = useActionStore((s) => s.closeAiChat);

  const isHubOpen = useHubStore((s) => s.isOpen);
  const teenWallEnabled = useTeenWallEnabled();
  const tabs = useMemo(
    () =>
      teenWallEnabled
        ? ACTION_TABS
        : ACTION_TABS.filter((tab) => tab.id !== "profile"),
    [teenWallEnabled],
  );
  const wallMentorshipId = teenWallEnabled ? mentorshipId : null;
  const profileDot = useMenteeWallBadges(wallMentorshipId).data?.hasUnacked;
  useWallUpdates(wallMentorshipId);
  useMentorshipUserUpdates(wallMentorshipId);

  const resolvedTab =
    !teenWallEnabled && activeTab === "profile"
      ? DEFAULT_ACTION_TAB
      : activeTab;
  const {
    actionWidth,
    actionPx,
    actionMax,
    actionOverlay,
    dragging,
    setActionWidth,
    setDragging,
    resetAction,
  } = useWorkflowColumnLayout();
  const drag = useRef({ width: actionWidth, collapsed, proposed: actionWidth });
  const actionFrame = useRef<HTMLElement>(null);
  const actionBounds = {
    rail: ACTION_RAIL,
    min: ACTION_MIN,
    max: ACTION_MAX,
    expandAt: 150,
  };

  useEffect(() => {
    if (isHubOpen && collapsed) expand();
  }, [isHubOpen, collapsed, expand]);

  const handleToggleCollapse = useCallback(() => {
    analytics.trackCollapseToggle(!collapsed);
    toggleCollapse();
  }, [analytics, collapsed, toggleCollapse]);

  const handleSelectTab = useCallback(
    (next: ActionTab) => {
      if (next !== activeTab) analytics.trackTabSwitch(activeTab, next);
      closeAiChat();
      setTab(next);
    },
    [activeTab, analytics, closeAiChat, setTab],
  );

  const handleQuickSelect = useCallback(
    (next: ActionTab) => {
      if (next !== activeTab) analytics.trackTabSwitch(activeTab, next);
      closeAiChat();
      setTab(next);
      expand();
    },
    [activeTab, analytics, closeAiChat, expand, setTab],
  );

  const handleOpenAiChat = useCallback(() => {
    analytics.trackAiOpen("button");
    openAiChat();
  }, [analytics, openAiChat]);

  if (!selectedConversation) return null;

  return (
    <aside
      ref={actionFrame}
      aria-label="Action"
      className={cn(
        "relative flex h-full shrink-0 flex-col overflow-hidden border-l border-surfaces-z4",
        !dragging && "transition-[width] duration-200 ease-out",
        actionOverlay &&
          !collapsed &&
          "absolute inset-y-0 right-0 z-20 bg-white shadow-xl",
        actionOverlay && isHubOpen && "inset-x-0",
      )}
      style={{
        width: isHubOpen
          ? actionOverlay
            ? "100%"
            : 500
          : collapsed
            ? ACTION_RAIL
            : actionOverlay
              ? actionWidth
              : actionPx,
      }}
    >
      {isHubOpen ? null : (
        <ColumnDivider
          label="Resize action"
          className="absolute -left-1 top-0 h-full"
          onDragStart={() => {
            const width = collapsed
              ? ACTION_RAIL
              : actionOverlay
                ? actionWidth
                : actionPx;
            drag.current = { width, collapsed, proposed: width };
            setDragging(true);
          }}
          onDrag={(delta) => {
            const proposed = drag.current.width - delta;
            drag.current.proposed = proposed;
            const preview = previewColumnWidth(proposed, drag.current.collapsed, {
              ...actionBounds,
              max: actionOverlay ? ACTION_MAX : actionMax,
            });
            if (actionFrame.current) {
              actionFrame.current.style.width = `${preview}px`;
            }
          }}
          onDragEnd={() => {
            const next = resolveColumnDrag(
              drag.current.proposed,
              drag.current.collapsed,
              actionWidth,
              {
                ...actionBounds,
                max: actionOverlay ? ACTION_MAX : actionMax,
              },
            );
            if (next.collapsed) collapse();
            else {
              expand();
              setActionWidth(next.width);
            }
            const px = next.collapsed
              ? ACTION_RAIL
              : actionOverlay
                ? next.width
                : Math.min(next.width, actionMax);
            if (actionFrame.current) {
              actionFrame.current.style.width = `${px}px`;
            }
            setDragging(false);
          }}
          onReset={() => {
            if (collapsed) expand();
            resetAction();
          }}
        />
      )}
      {collapsed && !isHubOpen ? (
        <CollapsedRail
          tabs={tabs}
          activeTab={resolvedTab}
          onExpand={handleQuickSelect}
          profileDot={profileDot}
        >
          <IconButton
            variant="tertiary"
            size="md"
            shape="circle"
            aria-label="Expand action panel"
            icon={<ChevronsLeft className="size-5" />}
            onClick={handleToggleCollapse}
          />
        </CollapsedRail>
      ) : (
        <ActionLaunchProvider
          menteeId={menteeId}
          mentorshipId={mentorshipId}
          teenName={menteeName}
          analytics={analytics}
        >
          {/* Keep Action mounted under Hub so tab local state survives open/close. */}
          <div
            className={cn(
              "relative flex h-full min-h-0 flex-col",
              isHubOpen && "pointer-events-none invisible",
            )}
            aria-hidden={isHubOpen}
          >
            <ActionHeader
              tabs={tabs}
              activeTab={resolvedTab}
              onSelectTab={handleSelectTab}
              onCollapse={handleToggleCollapse}
              profileDot={profileDot}
            />
            <div className="relative flex min-h-0 flex-1 flex-col">
              <ActionPanelScrollProvider>
                <ActionTabPanel
                  tab={resolvedTab}
                  capabilities={capabilities}
                  mentorshipId={mentorshipId}
                  menteeId={menteeId}
                  menteeName={menteeName}
                  mentorTimezone={mentorship?.mentor?.metadata?.timezone}
                  analytics={analytics}
                />
              </ActionPanelScrollProvider>
              {/* Outside the scroll region so it stays pinned to the panel. */}
              {resolvedTab === "history" ? (
                <ActionFloatingAdd analytics={analytics} />
              ) : null}
              {resolvedTab === "context" ? (
                <AiChatButton
                  aria-label="Ask AI about this mentee"
                  aria-expanded={aiChatOpen}
                  onClick={handleOpenAiChat}
                  className="absolute bottom-5 right-5 z-10"
                />
              ) : null}

              <div
                className={cn(
                  "absolute inset-0 z-20 bg-white",
                  !aiChatOpen && "pointer-events-none invisible",
                )}
                aria-hidden={!aiChatOpen}
              >
                <AiChatPanel
                  mentorshipId={mentorshipId}
                  teenName={menteeName}
                  onBack={closeAiChat}
                  active={aiChatOpen}
                  analytics={analytics}
                />
              </div>
            </div>
          </div>

          {isHubOpen ? (
            <div className="absolute inset-0 z-20 bg-white">
              <ActionHubOverlay mentorship={mentorship} />
            </div>
          ) : null}
        </ActionLaunchProvider>
      )}
    </aside>
  );
}

function ActionHubOverlay({
  mentorship,
}: {
  mentorship: MentorshipDetails | undefined;
}) {
  if (!mentorship) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }
  return <Hub mentorship={mentorship} />;
}

function ActionTabButton({
  tab,
  active,
  onSelect,
  showDot,
}: {
  tab: ActionTabConfig;
  active: boolean;
  onSelect: (id: ActionTab) => void;
  showDot?: boolean;
}) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      aria-label={tab.label}
      aria-pressed={active}
      onClick={() => onSelect(tab.id)}
      className={cn(
        "relative flex size-10 shrink-0 items-center justify-center rounded-full transition-colors",
        active
          ? "bg-surfaces-z3 shadow-[0px_1px_1.5px_rgba(0,0,0,0.06)]"
          : "border border-surfaces-divider bg-white hover:bg-surfaces-z0",
      )}
    >
      <Icon className="size-5 text-icon-primary" />
      {showDot ? (
        <span className="absolute -right-0.5 -top-0.5 size-3 rounded-full border-2 border-white bg-brand-periwinkle" />
      ) : null}
    </button>
  );
}

export function CollapsedRail({
  tabs,
  activeTab,
  onExpand,
  profileDot,
  children,
}: {
  tabs: readonly ActionTabConfig[];
  activeTab: ActionTab;
  onExpand: (tab: ActionTab) => void;
  profileDot?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-full flex-col items-center gap-2 py-3">
      {children}
      <div className="mt-1 flex flex-col items-center gap-2">
        {tabs.map((tab) => (
          <ActionTabButton
            key={tab.id}
            tab={tab}
            active={tab.id === activeTab}
            onSelect={onExpand}
            showDot={tab.id === "profile" && profileDot}
          />
        ))}
      </div>
    </div>
  );
}

export function ActionHeader({
  tabs,
  activeTab,
  onSelectTab,
  onCollapse,
  profileDot,
}: {
  tabs: readonly ActionTabConfig[];
  activeTab: ActionTab;
  onSelectTab: (tab: ActionTab) => void;
  onCollapse: () => void;
  profileDot?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-surfaces-z4 px-4 py-3">
      <IconButton
        variant="tertiary"
        size="md"
        shape="circle"
        aria-label="Collapse action panel"
        icon={<ChevronsRight className="size-5" />}
        onClick={onCollapse}
      />
      <div className="flex flex-1 items-center justify-end gap-2">
        {tabs.map((tab) => (
          <ActionTabButton
            key={tab.id}
            tab={tab}
            active={tab.id === activeTab}
            onSelect={onSelectTab}
            showDot={tab.id === "profile" && profileDot}
          />
        ))}
      </div>
    </div>
  );
}

function ActionTabPanel({
  tab,
  capabilities,
  mentorshipId,
  menteeId,
  menteeName,
  mentorTimezone,
  analytics,
}: {
  tab: ActionTab;
  capabilities: ActionSurfaceCapabilities;
  mentorshipId: string | null;
  menteeId: string | null;
  menteeName: string | null;
  mentorTimezone: string | null | undefined;
  analytics: ReturnType<typeof useActionAnalytics>;
}) {
  switch (tab) {
    case "profile":
      return (
        <ProfileTab
          mentorshipId={mentorshipId}
          menteeId={menteeId}
          menteeName={menteeName}
        />
      );
    case "context":
      return (
        <ContextTab
          capabilities={capabilities}
          mentorshipId={mentorshipId}
          analytics={analytics}
        />
      );
    case "tools":
      return <ToolsTab mentorshipId={mentorshipId} analytics={analytics} />;
    case "history":
      return (
        <HistoryTab
          menteeId={menteeId}
          mentorshipId={mentorshipId}
          timezone={mentorTimezone}
          analytics={analytics}
        />
      );
    default: {
      const _exhaustive: never = tab;
      return _exhaustive;
    }
  }
}
